import { Button } from '@/components/shadcn/ui/button';
import {
  mapFormDataToSignal,
  SignalAlertFormContent,
  signalAlertFormOptions,
  useCreateSignalAlertMutation,
} from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import { WORKFLOW_TYPE } from '@/types/workflowConst';

import { useSignalAlertTitles } from '../hooks/use-signal-alert-titles';

export type CreateSignalAlertDialogProps = {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  workspaceId: string;
  onViewList?: () => void;
  defaultDuration?: number;
};

export function CreateSignalAlertDialog({
  isOpen,
  close,
  unmount,
  workspaceId,
  onViewList,
  defaultDuration,
}: CreateSignalAlertDialogProps) {
  const createMutation = useCreateSignalAlertMutation();
  const { existingTitles } = useSignalAlertTitles();

  const form = useAppForm({
    ...signalAlertFormOptions,
    defaultValues: {
      ...signalAlertFormOptions.defaultValues,
      ...(defaultDuration != null && { duration: defaultDuration }),
    },
    onSubmit: async ({ value }) => {
      try {
        await createMutation.mutateAsync({
          title: value.title,
          enable: true,
          type: WORKFLOW_TYPE.SIGNAL_ALERT,
          signal: mapFormDataToSignal(value),
          channel: value.channel,
        });

        toast('Alert created', 'success', undefined, {
          actionLabel: 'View list',
          onClick: onViewList,
        });
        close();
      } catch (error) {
        toast(errorHandler(error, 'Failed to create signal alert'), 'fail');
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Create signal alert</DialogTitle>
          <DialogDescription>
            Get notified when unusual issues appear on the list.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0">
          <SignalAlertFormContent
            form={form}
            workspaceId={workspaceId}
            existingTitles={existingTitles}
            renderFooter={undefined}
          />
        </DialogBody>

        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  variant="default"
                  disabled={!canSubmit || isSubmitting}
                  onClick={form.handleSubmit}
                >
                  Create
                </Button>
              )}
            </form.Subscribe>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
